// Scroll is always native. All decorative layers are progressive enhancements.
(() => {
  const page = document.querySelector('.solvia-home');
  if (!page || page.dataset.cinematicReady || !('IntersectionObserver' in window)) return;
  page.dataset.cinematicReady = 'true';

  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = matchMedia('(hover: hover) and (pointer: fine)');
  const records = new Map();
  const visible = new Set();
  const moving = new Set();
  const animations = new Set();
  const entered = new WeakSet();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  let frame = 0;
  let previousTime = 0;

  function animate(element, keyframes, options) {
    if (motion.matches || !element.animate) return;
    const animation = element.animate(keyframes, options);
    animations.add(animation);
    const release = () => animations.delete(animation);
    animation.addEventListener('finish', release, { once: true });
    animation.addEventListener('cancel', release, { once: true });
  }

  function enter(record) {
    if (entered.has(record.element)) return;
    entered.add(record.element);
    if (motion.matches || document.hidden) return;
    const image = record.element.querySelector('img');
    if (record.type === 'hero' && image) {
      animate(image, [
        { clipPath: 'circle(15% at 68% 54%)', opacity: .45 },
        { clipPath: 'circle(125% at 68% 54%)', opacity: 1 },
      ], { duration: 1600, easing: 'cubic-bezier(.16, 1, .3, 1)' });
      animate(record.element.querySelector('.solvia-studio-orbit'), [
        { transform: 'translate(-50%, -50%) scale(.65) rotate(-30deg)', opacity: 0 },
        { opacity: .7, offset: .15 },
        { transform: 'translate(-50%, -50%) scale(3.8) rotate(55deg)', opacity: 0 },
      ], { duration: 2100, easing: 'cubic-bezier(.16, 1, .3, 1)' });
    }
    if (record.type === 'product' && image) {
      animate(image, [
        { clipPath: 'inset(12% 5% 12% 5% round 18px)', transform: 'scale(1.12)' },
        { clipPath: 'inset(0% 0% 0% 0% round 0px)', transform: 'scale(1)' },
      ], { duration: 1250, easing: 'cubic-bezier(.22, 1, .36, 1)' });
    }
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      const record = records.get(target);
      if (!record) return;
      if (isIntersecting) {
        visible.add(record);
        enter(record);
      } else {
        visible.delete(record);
        resetPointer(record);
      }
    });
    requestFrame();
  }, { threshold: 0 });

  function requestFrame() {
    if (!frame && !motion.matches && !document.hidden) frame = requestAnimationFrame(update);
  }

  function update(time) {
    frame = 0;
    if (motion.matches || document.hidden) return;
    // Time-based easing behaves the same on 60 Hz and high-refresh displays.
    const blend = 1 - Math.exp(-Math.min(time - previousTime || 16, 64) / 65);
    previousTime = time;
    const height = window.innerHeight;
    const writes = [];
    visible.forEach((record) => {
      const { element, type } = record;
      const bounds = element.getBoundingClientRect();
      if (type === 'heading') {
        const progress = clamp((height * .88 - bounds.top) / (height * .38));
        writes.push(() => element.style.setProperty('--solvia-heading-fill', `${(progress * 120).toFixed(1)}%`));
      }
      if (type === 'discover') {
        // A small, continuous camera move; the whole photo stays visible.
        const progress = clamp((height - bounds.top) / (height + bounds.height));
        writes.push(() => {
          element.style.setProperty('--solvia-scene-zoom', (1.065 - progress * .04).toFixed(4));
          element.style.setProperty('--solvia-scene-drift', `${((progress - .5) * 10).toFixed(2)}px`);
        });
      }
    });
    moving.forEach((record) => {
      if (!record.element.isConnected) { moving.delete(record); return; }
      record.x += (record.targetX - record.x) * blend;
      record.y += (record.targetY - record.y) * blend;
      if (Math.abs(record.x - record.targetX) + Math.abs(record.y - record.targetY) < .002) {
        record.x = record.targetX;
        record.y = record.targetY;
        moving.delete(record);
      }
      writes.push(() => {
        const style = record.element.style;
        style.setProperty('--solvia-light-x', `${((record.x + 1) * 50).toFixed(2)}%`);
        style.setProperty('--solvia-light-y', `${((record.y + 1) * 50).toFixed(2)}%`);
        style.setProperty('--solvia-tilt-x', `${(-record.y * 3).toFixed(2)}deg`);
        style.setProperty('--solvia-tilt-y', `${(record.x * 4).toFixed(2)}deg`);
      });
    });
    writes.forEach((write) => write());
    if (moving.size) requestFrame();
  }

  function resetPointer(record) {
    if (!record.pointer) return;
    record.element.classList.remove('solvia-pointer-active');
    record.targetX = record.targetY = 0;
    moving.add(record);
    requestFrame();
  }

  function register(element, type) {
    if (records.has(element)) return;
    const record = { element, type, x: 0, y: 0, targetX: 0, targetY: 0 };
    records.set(element, record);
    if (type === 'heading') element.classList.add('solvia-scroll-heading');
    if (type === 'hero' || type === 'product') {
      record.pointer = true;
      record.events = new AbortController();
      const options = { passive: true, signal: record.events.signal };
      element.classList.add('solvia-pointer-surface');
      element.addEventListener('pointermove', (event) => {
        if (motion.matches || !pointer.matches || event.pointerType === 'touch') return;
        const bounds = element.getBoundingClientRect();
        record.targetX = clamp((event.clientX - bounds.left) / bounds.width * 2 - 1, -1, 1);
        record.targetY = clamp((event.clientY - bounds.top) / bounds.height * 2 - 1, -1, 1);
        element.classList.add('solvia-pointer-active');
        moving.add(record);
        requestFrame();
      }, options);
      element.addEventListener('pointerleave', () => resetPointer(record), options);
      element.addEventListener('pointercancel', () => resetPointer(record), options);
      if (type === 'hero') {
        const orbit = document.createElement('span');
        orbit.className = 'solvia-studio-orbit';
        orbit.setAttribute('aria-hidden', 'true');
        element.append(orbit);
      }
    }
    observer.observe(element);
  }

  function initialize(root = page) {
    root.querySelectorAll('.solvia-hero__visual').forEach((element) => register(element, 'hero'));
    root.querySelectorAll('.solvia-showcase--models .solvia-product-tile__image').forEach((element) => register(element, 'product'));
    root.querySelectorAll('.solvia-discover__scene').forEach((element) => register(element, 'discover'));
    root.querySelectorAll('.solvia-section-heading h2, .solvia-everyday h2, .solvia-first-steps h2, .solvia-faq__intro h2').forEach((element) => register(element, 'heading'));
    requestFrame();
  }

  function syncMotion() {
    page.classList.toggle('solvia-cinematic-enabled', !motion.matches);
    records.forEach(resetPointer);
    animations.forEach((animation) => animation.cancel());
    if (motion.matches) {
      cancelAnimationFrame(frame);
      frame = 0;
      moving.clear();
    }
    requestFrame();
  }

  // In the Shopify editor, show the complete image and every control at once.
  page.classList.toggle('solvia-cinematic-enabled', !motion.matches);
  initialize();
  motion.addEventListener('change', syncMotion);
  pointer.addEventListener('change', () => records.forEach(resetPointer));
  window.addEventListener('scroll', requestFrame, { passive: true });
  window.addEventListener('resize', requestFrame, { passive: true });
  window.addEventListener('pageshow', requestFrame);
  document.addEventListener('visibilitychange', () => {
    animations.forEach((animation) => document.hidden ? animation.pause() : animation.play());
    if (document.hidden) {
      cancelAnimationFrame(frame);
      frame = 0;
      records.forEach(resetPointer);
    } else requestFrame();
  });
  // Content/fonts and sticky header sizing can change without a window resize.
  if ('ResizeObserver' in window) {
    const resize = new ResizeObserver(requestFrame);
    resize.observe(page);
    const header = page.querySelector('.section-header');
    if (header) resize.observe(header);
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    records.forEach((record, element) => {
      if (!event.target.contains(element)) return;
      observer.unobserve(element);
      record.events?.abort();
      visible.delete(record);
      moving.delete(record);
      records.delete(element);
    });
    animations.forEach((animation) => {
      if (event.target.contains(animation.effect?.target)) animation.cancel();
    });
  });
})();
